import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  connectionApi,
  type Connection,
  type ConnectionAuth,
  type ConnectionEndpoint,
} from '../../lib/connections-api';
import styles from './Connections.module.css';

export interface CredentialInput {
  value: () => ConnectionAuth;
  clear: () => void;
}
// Secret material lives only in short-lived component refs, never query keys,
// mutation variables, browser storage, page context, or returned API data.
export const ConnectionCredentials = forwardRef<
  CredentialInput,
  { initialMode?: 'none' | 'bearer' | 'mtls' }
>(function ConnectionCredentials({ initialMode = 'none' }, ref) {
  const [mode, setMode] = useState(initialMode);
  const secret = useRef({
    token: '',
    certificateChainPem: '',
    privateKeyPem: '',
  });
  const generation = useRef(0);
  const token = useRef<HTMLInputElement>(null);
  const [names, setNames] = useState({ certificate: '', key: '' });
  const [error, setError] = useState('');
  const clear = () => {
    generation.current++;
    secret.current = { token: '', certificateChainPem: '', privateKeyPem: '' };
    if (token.current) token.current.value = '';
    setNames({ certificate: '', key: '' });
  };
  useImperativeHandle(
    ref,
    () => ({
      value: () =>
        mode === 'none'
          ? { mode }
          : mode === 'bearer'
            ? { mode, token: secret.current.token }
            : {
                mode,
                certificateChainPem: secret.current.certificateChainPem,
                privateKeyPem: secret.current.privateKeyPem,
              },
      clear,
    }),
    [mode],
  );
  const importFile = async (
    file: File | undefined,
    kind: 'certificate' | 'key',
  ) => {
    if (!file) return;
    if (file.size > (kind === 'certificate' ? 48000 : 16000)) {
      setError('The PEM file exceeds the import limit.');
      return;
    }
    const revision = generation.current;
    const text = await file.text();
    if (revision !== generation.current) return;
    if (kind === 'key' && text.includes('ENCRYPTED')) {
      setError(
        'Use an unencrypted PEM private key. Encrypted PEM and PKCS#12 imports are not supported.',
      );
      return;
    }
    secret.current[
      kind === 'certificate' ? 'certificateChainPem' : 'privateKeyPem'
    ] = text;
    setNames((value) => ({ ...value, [kind]: file.name }));
    setError('');
  };
  return (
    <fieldset className={styles.credentialFields}>
      <legend>Authentication</legend>
      <label>
        Credential type
        <select
          value={mode}
          onChange={(event) => {
            clear();
            setMode(event.target.value as typeof mode);
          }}
        >
          <option value="none">No credential</option>
          <option value="bearer">Access token / API key</option>
          <option value="mtls">Client certificate (mTLS)</option>
        </select>
      </label>
      {mode === 'bearer' && (
        <label>
          Access token or API key
          <input
            ref={token}
            type="password"
            autoComplete="off"
            onChange={(event) => {
              secret.current.token = event.target.value;
            }}
          />
        </label>
      )}
      {mode === 'mtls' && (
        <>
          <label>
            PEM certificate / chain
            <input
              type="file"
              accept=".pem,.crt"
              onChange={(event) => {
                void importFile(event.target.files?.[0], 'certificate');
                event.target.value = '';
              }}
            />
          </label>
          {names.certificate && <span className={styles.importedFile}>{names.certificate}</span>}
          <label>
            PEM private key
            <input
              type="file"
              accept=".pem,.key"
              onChange={(event) => {
                void importFile(event.target.files?.[0], 'key');
                event.target.value = '';
              }}
            />
          </label>
          {names.key && <span className={styles.importedFile}>{names.key}</span>}
          <p className={styles.muted}>
            Import the dedicated credential for this connection. Compare the
            fingerprint after saving. Its private key stays on the server;
            replace it here when renewing.
          </p>
        </>
      )}
      {error && <p role="alert">{error}</p>}
    </fieldset>
  );
});

export function EndpointCredential({
  connection,
  endpoint,
  onSaved,
}: {
  connection: Connection;
  endpoint: ConnectionEndpoint;
  onSaved: (value: Connection) => void;
}) {
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const input = useRef<CredentialInput>(null);
  const auth = endpoint.auth;
  const expiry = auth?.expiresAt && Number.isFinite(Date.parse(auth.expiresAt))
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(auth.expiresAt)) : null;
  const save = async () => {
    if (!input.current) return;
    setBusy(true);
    setError('');
    try {
      const value = await connectionApi.replaceCredential(
        connection.id,
        endpoint.id,
        connection.revision,
        input.current.value(),
      );
      onSaved(value);
      setOpen(false);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Credential replacement failed.',
      );
    } finally {
      input.current?.clear();
      setBusy(false);
    }
  };
  return (
    <div className={styles.endpointCredential}>
      <strong>
        {auth?.mode === 'mtls'
          ? 'Client certificate (mTLS)'
          : auth?.mode === 'bearer'
            ? 'Access token'
            : 'No credential'}
      </strong>
      {expiry && <span className={styles.credentialExpiry}>Expires {expiry}</span>}
      {auth?.expiresAt && Date.parse(auth.expiresAt) <= Date.now()
        ? <span className={styles.error}>Certificate expired · Replace to reconnect</span>
        : auth?.renewalWarning && <span className={styles.credentialExpiry}>Renew within 14 days</span>}
      {auth?.fingerprint && (
        <details className={styles.certificateDetails}>
        <summary>Certificate details</summary>
        <p className={styles.muted}>
          Fingerprint: <code>{auth.fingerprint}</code>
          <br />
          Expires: {auth.expiresAt}
          {auth.expiresAt && Date.parse(auth.expiresAt) <= Date.now()
            ? ' · Certificate expired; replace it'
            : auth.renewalWarning
              ? ' · Renew within 14 days'
              : ''}
        </p>
        </details>
      )}
      {!open ? (
        <button
          type="button"
          disabled={!!connection.disconnected_at}
          onClick={() => setOpen(true)}
        >
          Replace credential
        </button>
      ) : (
        <div className={styles.credentialReplacement}>
          <ConnectionCredentials
            ref={input}
            initialMode={auth?.mode ?? 'none'}
          />
          <p className={styles.muted}>
            Replacing a credential cancels unused approvals. Confirm this is
            the credential for {endpoint.url} before saving.
          </p>
          <div className={styles.actions}>
          <button type="button" disabled={busy} onClick={() => void save()}>
            {busy ? 'Checking…' : 'Replace and check connection'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              input.current?.clear();
              setOpen(false);
            }}
          >
            Cancel
          </button>
          </div>
        </div>
      )}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
